import _ from "lodash";
import {
  CaretDownFilled,
  DeleteOutlined,
  PencilLineOutlined,
  PencilOutlined,
} from "@toddle-edu/ds-icons";
import { Button, IconButton, DatePicker } from "@toddle-edu/ds-web";
import classNames from "classnames";
import { useI18n } from "Hooks";
import React, { useMemo } from "react";
import ProfileCascade from "UIComponents/ProfileCascade";
import Textview from "UIComponents/Textview";
import { AttachmentList } from "Attachment";
import { generateRandomId, getDateAndTimeSince } from "Utils";
import {
  getFormattedEvidence,
  getFilteredAttachmentsFromEvidencesMemoized,
} from "Utils/DataRestructure";
import classes from "./ResponseItem.scss";
import ChatbarWrapper from "UIComponents/ChatbarWrapper/ChatbarWrapper";
import {
  updateLocalResponses,
  deleteAttachments,
  editResponse,
} from "IBPlanner/modules/IBPlannerModule";
import { compose } from "lib/apolloClient";
import { connect } from "react-redux";
import moment from "moment";
import { isReflectionCreatedAtEditEnabled } from "UnitPlans/modules/Utils";
import { getDefaultStudentNameOrderPreference } from "modules/CommonSelectors";
import { getFormattedUserName } from "Utils/GetFormattedUserName";
import ACLStore from "lib/aclStore";

const modalDimensions = {
  height: "fit-content",
  width: "598px",
};

const getAvatarStyles = () => ({
  shape: "square",
  size: "medium",
  color: "neutral",
  iconType: "none",
  shade: "light",
  randomizeColor: true,
});

const DEFAULT_MESSAGE_DATA = {
  label: "",
  attachments: [],
  id: generateRandomId(),
};

const getOptions = ({
  isSinglePageAndNewUnitView,
  showOnlyDeleteOption,
  t,
}) => {
  const deleteDropdownOption = {
    label: t("delete"),
    key: "DELETE",
    isDestructive: true,
    icon: (
      <DeleteOutlined
        variant={isSinglePageAndNewUnitView ? "subtle" : "critical"}
        className={classes.icon}
      />
    ),
  };

  return showOnlyDeleteOption
    ? [deleteDropdownOption]
    : [
        {
          label: t("edit"),
          key: "EDIT",
          icon: (
            <PencilLineOutlined variant="subtle" className={classes.icon} />
          ),
        },
        deleteDropdownOption,
      ];
};

const getOptionsMemoized = _.memoize(
  params => getOptions(params),
  params => JSON.stringify(params)
);

const ResponseItem = React.memo(
  ({
    response,
    isActive,
    onClickEdit,
    onClickDelete,
    onMediaClick,
    onCancelEdit,
    onSaveEditClicked,
    localResponseObj,
    dropDownOptions,
    onDropDownOptionClick,
    onResponseChange,
    mode = "view",
    onAddAttachment,
    onDeleteMediaClick,
    isAdmin = false,
    userInfo,
    createDropDownRef,
    onBlurCreate,
    onEditClickFromViewMode,
    fieldId,
    updateLocalResponses,
    deleteAttachments,
    editResponse,
    onResponseUpdatedAtChange,
    defaultStudentNameOrderPreference,
    showResponseEditButton,
    testId,
    isSinglePageAndNewUnitView,
    getFilteredIdsFromEvidence,
    isReflectionField,
  }) => {
    const { id: userId, user_type } = userInfo;

    const { t } = useI18n(["common"]);

    const isEditMode = mode === "edit";

    // Derive chatBarData directly from props
    const chatBarData = useMemo(() => {
      if (!isActive) return DEFAULT_MESSAGE_DATA;

      return {
        ...localResponseObj,
        label: localResponseObj.message,
        attachments: getFilteredAttachmentsFromEvidencesMemoized({
          evidence: localResponseObj.evidence,
        }),
      };
    }, [localResponseObj, isActive]);

    const { id: responseId, createdBy, createdAt, updatedAt } = response;

    const { message = "", evidence = [] } = response;
    const totalAttachments = _.get(evidence, "length", 0);
    const { id: creatorId, firstName, profileImage, lastName } = createdBy;
    const showOnlyDeleteOption = isEditMode && isAdmin && userId !== creatorId;

    const onActionClick = key => {
      switch (key) {
        case "EDIT":
          onClickEdit(response);
          break;
        case "DELETE":
          onClickDelete({ id: responseId });
          break;
      }
    };

    const showContainerWithPadding = _.includes(
      ["student", "parent"],
      user_type
    );

    const chatBarProps = {};
    if (isSinglePageAndNewUnitView) {
      chatBarProps.dsVersion = "2.0";
    }

    const containerClass = classNames(classes.container, {
      [classes.activeCotainer]: isActive,
      [classes.activeContainerV2]: isActive && isSinglePageAndNewUnitView,
      [classes.containerWithPadding]:
        showContainerWithPadding && !isSinglePageAndNewUnitView,
      [classes.hoveredClass]: !isEditMode && showResponseEditButton,
      [classes.containerV2]: isSinglePageAndNewUnitView,
    });

    const profileCascadeItems = [
      { id: creatorId, value: firstName, imageUrl: profileImage },
    ];
    const shouldDisplayRightContainer =
      !isActive && isEditMode && (isAdmin || userId === creatorId);

    const onEditButtonClick = e => {
      if (!showResponseEditButton) return;
      if (e) e.stopPropagation();
      updateLocalResponses({
        [fieldId]: _.cloneDeep(response),
      });
      onEditClickFromViewMode();
    };
    const customAttachmentContainerStyles = {
      gridTemplateColumns: totalAttachments > 1 ? `1fr 1fr` : `1fr`,
      gap: "8px",
    };

    const showEditDateIcon = isReflectionCreatedAtEditEnabled();

    const updatedAtDateMomentObj = moment(
      localResponseObj?.updatedAt || updatedAt || createdAt
    );

    const getDateAndTimeComponent = () => {
      return showEditDateIcon ? (
        <>
          <div
            className={classNames(classes.subtext, {
              "text-body-3": !isSinglePageAndNewUnitView,
              "text-body": isSinglePageAndNewUnitView && !isActive,
              "text-label": isSinglePageAndNewUnitView && isActive,
              [classes.dateTimeContainer]:
                isSinglePageAndNewUnitView && isActive,
            })}
          >
            {isSinglePageAndNewUnitView && isActive && (
              <div
                className={
                  isSinglePageAndNewUnitView ? "text-label" : "text-label-4"
                }
              >
                {t("common:date_slash_time")}
              </div>
            )}

            {isSinglePageAndNewUnitView && isActive ? (
              <div>
                <DatePicker
                  picker="date"
                  value={moment(localResponseObj?.updatedAt)}
                  onChange={date => {
                    onResponseUpdatedAtChange({ updatedAt: date });
                  }}
                  showTimePicker={true}
                  testId={testId}
                >
                  <div className={classes.editDateContainer}>
                    <div
                      className={classNames({
                        [classes.dateTimeText]:
                          isSinglePageAndNewUnitView && isActive,
                      })}
                    >
                      {moment(updatedAtDateMomentObj).format(
                        "h:mm a, Do MMM YYYY"
                      )}
                    </div>
                    <IconButton
                      icon={<CaretDownFilled />}
                      _legacy_variant="plain"
                      size="x-small"
                      testId={`${testId}-editDate`}
                      variant="neutral"
                      type="plain"
                    />
                  </div>
                </DatePicker>
              </div>
            ) : (
              <span
                className={classNames({
                  [classes.dateTimeText]:
                    isSinglePageAndNewUnitView && isActive,
                })}
              >
                {moment(updatedAtDateMomentObj).format("h:mm a, Do MMM YYYY")}
              </span>
            )}
          </div>
          {isActive && !isSinglePageAndNewUnitView ? (
            <div>
              <DatePicker
                picker="date"
                value={moment(localResponseObj?.updatedAt)}
                onChange={date => {
                  onResponseUpdatedAtChange({ updatedAt: date });
                }}
                showTimePicker={true}
                testId={testId}
              >
                <IconButton
                  icon={<CaretDownFilled />}
                  _legacy_variant="plain"
                  size="x-small"
                  testId={`${testId}-editDate`}
                  variant="neutral"
                  type="plain"
                />
              </DatePicker>
            </div>
          ) : null}
        </>
      ) : (
        <div
          className={classNames(classes.subtext, {
            "text-body-3": !isSinglePageAndNewUnitView,
            "text-body": isSinglePageAndNewUnitView,
          })}
        >
          <span>
            {getDateAndTimeSince({
              date: new Date(updatedAtDateMomentObj),
              format: "h:mm a, Do MMM YYYY",
              t,
            })}
          </span>
        </div>
      );
    };

    const handleDeleteAttachment = attachment => {
      // Find the evidence item based on the attachment ID
      const evidenceItem = evidence.find(
        item =>
          item.id === attachment.attachmentId ||
          (item.attachments &&
            item.attachments.some(att => att.id === attachment.attachmentId))
      );

      if (!evidenceItem) return;

      // Check if the attachment is a post using the __typename property
      const isPostAttachment = evidenceItem.__typename === "Post";

      // If it's a post, we need to use editResponse to remove that post
      if (isPostAttachment) {
        const updatedEvidence = evidence.filter(
          item => item.id !== evidenceItem.id
        );

        // If this would result in an empty response (no message and no attachments), delete the whole response
        const isMessageEmpty = !_.trim(message);
        const wouldHaveNoEvidence = updatedEvidence.length === 0;

        if (isMessageEmpty && wouldHaveNoEvidence) {
          onClickDelete({ id: responseId });
        } else {
          const updatedResponse = {
            ...response,
            evidence: updatedEvidence,
          };

          const initialPostIds = getFilteredIdsFromEvidence({
            responseId,
            isPost: true,
          });
          const initialAttachmentIds = getFilteredIdsFromEvidence({
            responseId,
            isPost: false,
          });

          editResponse({
            fieldId,
            id: responseId,
            initialPostIds,
            initialAttachmentIds,
            responseFieldData: updatedResponse,
          });
        }
      } else {
        // Handle regular attachment deletion
        const updatedEvidence = evidence.filter(
          item => item.id !== attachment.attachmentId
        );

        // Check if this would result in an empty response
        const isMessageEmpty = !_.trim(message);
        const wouldHaveNoEvidence = updatedEvidence.length === 0;

        if (isMessageEmpty && wouldHaveNoEvidence) {
          onClickDelete({ id: responseId });
        } else {
          // Use the existing deleteAttachments function for non-post attachments
          deleteAttachments({
            attachmentIds: [attachment.attachmentId],
            id: fieldId,
            responseId: responseId,
          });
        }
      }
    };

    const reflectionResponseCreatedBy =
      isReflectionField && _.get(response, "createdBy.id", "");

    return (
      <div className={containerClass} onClick={onEditButtonClick} tabIndex={0}>
        <div
          className={classNames(classes.profileContainer, {
            [classes.flexibleWidth]: isSinglePageAndNewUnitView,
          })}
        >
          <ProfileCascade
            items={profileCascadeItems}
            avatarStyles={getAvatarStyles(isSinglePageAndNewUnitView)}
            showAvatar={true}
            dsVersion={isSinglePageAndNewUnitView ? "2.0" : "1.0"}
          />
        </div>
        <div
          className={classNames(classes.infoContainer, {
            [classes.singlePageInfoContainer]:
              isSinglePageAndNewUnitView && isActive,
          })}
        >
          {/* Header */}
          <div
            className={classNames(classes.headerContainer, {
              [classes.headerContainerV2]:
                isSinglePageAndNewUnitView && !isActive,
              "text-label": isSinglePageAndNewUnitView && !isActive,
            })}
          >
            {/* Name and time */}
            <div className={classes.nameClassContainer}>
              {!isActive && isSinglePageAndNewUnitView && (
                <div className={classNames("text-label", classes.nameText)}>
                  {getFormattedUserName({ user: createdBy })}
                </div>
              )}
              {isActive && isSinglePageAndNewUnitView && !showEditDateIcon && (
                <div className={classNames("text-label", classes.nameText)}>
                  {getFormattedUserName({ user: createdBy })}
                </div>
              )}
              {!isSinglePageAndNewUnitView && (
                <div className={classNames("text-label-2", classes.nameText)}>
                  {getFormattedUserName({ user: createdBy })}
                </div>
              )}
              {ACLStore.can("FeatureFlag:EnableDateTimeInReflections")
                ? getDateAndTimeComponent()
                : null}
            </div>
            {/* right container */}
            <div className={classes.rightContainer}>
              {shouldDisplayRightContainer ? (
                <div className={classes.icons}>
                  {/*  Display edit or/and delete icons on hover on right side of card for edit mode*/}
                  {_.map(
                    getOptionsMemoized({
                      isSinglePageAndNewUnitView,
                      showOnlyDeleteOption,
                      t,
                    }),
                    item => {
                      return (
                        <IconButton
                          key={item.key}
                          size="x-small"
                          variant="neutral"
                          type="plain"
                          dsVersion="2.0"
                          icon={item.icon}
                          onClick={() => onActionClick(item.key)}
                          testId={`${testId}-${_.camelCase(item.key)}`}
                        />
                      );
                    }
                  )}
                </div>
              ) : showResponseEditButton ? (
                <Button
                  variant="inline"
                  size="small"
                  icon={<PencilOutlined />}
                  onClick={onEditButtonClick}
                  testId={`${testId}-edit}`}
                >
                  {t("common:edit")}
                </Button>
              ) : null}
            </div>
          </div>

          {/* Content */}

          {isActive && chatBarData ? (
            <div
              className={classNames(classes.lowerContainer, {
                [classes.lowerActiveContainer]: isSinglePageAndNewUnitView,
                ["text-body-1"]: !isSinglePageAndNewUnitView,
                ["text-body"]: isSinglePageAndNewUnitView,
              })}
            >
              <ChatbarWrapper
                {...chatBarProps}
                messageData={chatBarData}
                sendMessage={onSaveEditClicked}
                inputTextPlaceHolder={t("common:write_your_reflection_here")}
                cancelEditMessage={onCancelEdit}
                collaborators={[]}
                mode={"EDIT"}
                selectDropDownOptions={dropDownOptions}
                onDropDownOptionClick={onDropDownOptionClick}
                onResponseChange={onResponseChange}
                onAddAttachment={onAddAttachment}
                onDeleteMediaClick={onDeleteMediaClick}
                onMediaClick={onMediaClick}
                customDropdownRef={createDropDownRef}
                onBlur={onBlurCreate}
                isStickyContainer={false}
                showAccessibilityHelperText={false}
                chatBarContainerStyle={
                  isSinglePageAndNewUnitView && isActive && showEditDateIcon
                    ? {
                        borderTopLeftRadius: "0px",
                        borderTopRightRadius: "0px",
                      }
                    : {}
                }
                testIdPrefix={testId}
                containerStyle={
                  isSinglePageAndNewUnitView && showEditDateIcon
                    ? {
                        borderTop: "0px",
                        borderTopLeftRadius: "0px",
                        borderTopRightRadius: "0px",
                      }
                    : {}
                }
                modalDimensions={modalDimensions}
              />
            </div>
          ) : (
            <div
              className={classNames(classes.responseContainer, {
                [classes.responseContainerV2]:
                  isSinglePageAndNewUnitView && !isActive,
              })}
            >
              <div>
                <Textview
                  mode={"view"}
                  showViewEmpty={false}
                  value={message}
                  isWordWrapRequired={true}
                  isSinglePageAndNewUnitView={isSinglePageAndNewUnitView}
                />
              </div>
              {isSinglePageAndNewUnitView &&
                !isActive &&
                !message &&
                totalAttachments > 0 && (
                  <div className={classNames(classes.fileCount, "text-body")}>
                    {`${t("common:attached_file_count", {
                      count: totalAttachments,
                    })}`}
                  </div>
                )}
              {totalAttachments ? (
                <div
                  className={classNames(
                    (classes.lowerContainer,
                    {
                      [classes.lowerContainerV2]: isSinglePageAndNewUnitView,
                    })
                  )}
                >
                  <AttachmentList
                    cardType={"design-media-card"}
                    value={getFormattedEvidence({ evidence })}
                    showCreateDropdown={false}
                    onClickAttachmentCard={onMediaClick}
                    mode={"view"}
                    showAttachmentAsPost={true}
                    {...(isSinglePageAndNewUnitView
                      ? {
                          isSinglePageAndNewUnitView,
                          isDeleteEnabled: false,
                          showDeleteForSelfInViewMode: false,
                          alwaysShowActions: false,
                          dsVersion: "2.0",
                          showActionsOnHover: true,
                        }
                      : {
                          containerStyle: customAttachmentContainerStyles,
                        })}
                  />
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
    );
  }
);

const mapStateToProps = state => {
  const defaultStudentNameOrderPreference = getDefaultStudentNameOrderPreference(
    state
  );

  return {
    defaultStudentNameOrderPreference,
  };
};

const mapActionCreatorsToProps = {
  updateLocalResponses,
  deleteAttachments,
  editResponse,
};

ResponseItem.displayName = "ResponseItem";

ResponseItem.defaultProps = {
  response: {},
  localResponseObj: {},
  mode: "view",
  isAdmin: false,
  onEditClickFromViewMode: () => {},
  showResponseEditButton: false,
};

export default compose(connect(mapStateToProps, mapActionCreatorsToProps))(
  ResponseItem
);
